import React, { useMemo, useContext } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import numeral from 'numeral';
import { Tooltip } from '@mui/material';

import AMRPredictionsBlock from '../AMRPredictions/AMRPredictionsBlock.jsx';
import resistanceGenesReference from './../AMRPredictions/resistanceGenesReference.js'
import { identifiedSpeciesIsVirus, formatSpeciesName } from './utilities.js';
import { IdentifiedSpeciesFiltersContext } from '../context/IdentifiedSpeciesFiltersContext.js';

import { useResistanceGenes, useAmrPredictions, useImputations } from '../services/api/KeynomeAPIHooks.js';

function IdentifiedSpeciesRow(props) {
  const {
    isIllumina,
    identifiedSpecies,
    isDiagnosticView,
    totalMicrobialMegabases,
  } = props;

  const {
    showAST,
    showKIDCalls,
    showRnDModels,
    showImputedCalls,
    showAlignmentMetrics,
    showCoverageBreadth,
    showCoverageDepth,
    showAssemblyMetrics,
    showAssemblyMBsToSpecies,
    showContigsToSpecies,
    showResistanceGenes,
  } = useContext(IdentifiedSpeciesFiltersContext);

  let showKIDColumns = identifiedSpecies.attributes.species_call_raw_value !== null;

  const speciesCallRank = identifiedSpecies?.attributes.species_call_rank_by_kid_value ?? identifiedSpecies?.attributes.species_call_rank_by_reads;
  const speciesCallPositive = identifiedSpecies?.attributes.species_call_positive

  // Only load AMR predictions if Illumina sample or species call positive
  const { data: amrPredictions } = useAmrPredictions((speciesCallPositive || isIllumina) && identifiedSpecies?.id);

  const highConfidencePredictions = amrPredictions?.filter(item => ['VH', 'H'].includes(item.attributes.model_performance_label));
  const mediumConfidencePredictions = amrPredictions?.filter(item => item.attributes.model_performance_label === 'M');

  const { data: resistanceGenes, isLoading: resistanceGenesLoading } = useResistanceGenes(
      identifiedSpecies?.id,
      {},
      // Only load resistance genes if AMR predictions are available
      !!amrPredictions && amrPredictions.length > 0
  );
  const { data: imputations } = useImputations(
      highConfidencePredictions?.map(prediction => prediction.id),
      // Only load imputations if in top 5 calls
      (speciesCallRank || 6) <= 5
  );

  const resistanceGeneHierarchyNodes = useMemo(() => {
      if (resistanceGenesLoading || !!!resistanceGenes) return [];

      const hierarchyNodes = resistanceGenes.reduce((acc, resGene) => {
          const node = resGene.attributes.hierarchy_node;
          if (!!node && !acc.has(node)) acc.add(node);
          return acc;
      }, new Set());
      return Array.from(hierarchyNodes);
  }, [resistanceGenes, resistanceGenesLoading]);

  const visiblePredictions = useMemo(() => {
      let predictions = []
      const predictedDrugs = new Set();

      if (!!highConfidencePredictions) {
          predictions = predictions.concat(highConfidencePredictions);
          highConfidencePredictions.forEach(prediction => predictedDrugs.add(prediction.attributes.model_drug));
      }

      if (showRnDModels && !!mediumConfidencePredictions) {
          predictions = predictions.concat(mediumConfidencePredictions);
          mediumConfidencePredictions.forEach(prediction => predictedDrugs.add(prediction.attributes.model_drug));
      }

      if (showImputedCalls && !!imputations) {
          const displayableImputations = imputations.filter(imputation => !!imputation && !predictedDrugs.has(imputation.attributes.drug));
          predictions = predictions.concat(displayableImputations);
          displayableImputations.forEach(imputation => predictedDrugs.add(imputation.attributes.drug));
      }

      if (showResistanceGenes && !!resistanceGeneHierarchyNodes) {
          // Create AMRPredictionRow for each resistance gene associated with
          // an antibotic not represented by an AMR prediction or imputation
          const resistanceGenePredictions = Object.values(resistanceGeneHierarchyNodes.reduce((acc, node) => {
              const nodeDetails = resistanceGenesReference[node];
              if (!nodeDetails) return acc;
              return nodeDetails.antibiotics
                .filter(drug => !predictedDrugs.has(drug))
                .reduce((innerAcc, drug) => {
                  if (!innerAcc[drug]) {
                      innerAcc[drug] = {drug: drug, resistanceGenes: []};
                  }
                  innerAcc[drug].resistanceGenes.push(nodeDetails.display_name);
                  return innerAcc
              }, acc);
          }, []));

          predictions = predictions.concat(resistanceGenePredictions)
      }

      predictions = predictions.filter(prediction => {
          const drug = prediction.attributes?.model_drug || prediction.attributes?.drug || prediction.drug
          return drug !== 'cefoxitin' && drug !== 'methicillin'
      })

      predictions.sort((a, b) => {
          const aDrug = a.attributes?.model_drug || a.attributes?.drug || a.drug
          const bDrug = b.attributes?.model_drug || b.attributes?.drug || b.drug
          return aDrug.localeCompare(bDrug)
        })

      return predictions;
      
  }, [showRnDModels, showImputedCalls, showResistanceGenes, highConfidencePredictions, mediumConfidencePredictions, imputations, resistanceGeneHierarchyNodes])

  const amrPredictionsById = useMemo(() => {
      return amrPredictions?.reduce((acc, prediction) => {
          acc[prediction.id] = prediction;
          return acc;
      }, {});
  }, [amrPredictions]);

  let amrPredictionsContent = null;
  if (showAST && (speciesCallPositive || isIllumina)) {
      amrPredictionsContent = <AMRPredictionsBlock 
        isDiagnosticView={isDiagnosticView}
        visibleAmrPredictions={visiblePredictions}
        qualityLimitations={identifiedSpecies.attributes.reasons_for_qc_failure} 
        amrPredictionsReference={amrPredictionsById}
        resistanceGenes={resistanceGeneHierarchyNodes}
      />
  }

  const speciesName = formatSpeciesName(identifiedSpecies.attributes.species);
  const speciesNameContent = (identifiedSpecies.attributes.species_call_positive || (isIllumina && identifiedSpecies.attributes.reads_frac_agreeing_megabases >= 0.8))
    ? (<p style={{overflow: 'hidden', whiteSpace: 'nowrap', textOverflow:'ellipsis'}}><b>{speciesName}</b></p>)
    : (<p style={{ fontWeight: (showKIDCalls || isIllumina) ? null : 'bold', color: (showKIDCalls || isIllumina) ? 'gray' : null, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow:'ellipsis' }}>{speciesName}</p>);

  let positivityContent = null;
  if (!isDiagnosticView && showKIDCalls && showKIDColumns) {
    positivityContent = (
      <Grid item xs={12} sm={1}>
        {identifiedSpecies.attributes.species_call_positive
      ? (<p style={{ color: 'green' }}><b>Positive </b></p>)
      : (
        <p style={{ color: 'gray' }}>
          Subthreshold
        </p>
      )}
      </Grid>
    );
  }


  const detailContentColor = (identifiedSpecies.attributes.species_call_positive || (!showKIDCalls && !isIllumina) || (isIllumina && identifiedSpecies.attributes.reads_frac_agreeing_megabases >= 0.8)) ? null : 'gray';

  let kidValueContent = null;
  if (!isDiagnosticView && identifiedSpecies.attributes.species_call_raw_value !== null) {
    kidValueContent = (
      <Grid item xs={12} sm={2}>
        <p style={{ color: detailContentColor, marginTop: '5px', marginBottom: '5px' }}><small><b>
          {identifiedSpecies.attributes.species_call_raw_value < 0.001 ? '<0.001' : Math.min(identifiedSpecies.attributes.species_call_raw_value, 1.000).toFixed(3)}
            <br /> KID value
        </b></small></p>
      </Grid>
    );
  }

  let readsToSpeciesContent = null;
  if (!isDiagnosticView && !showKIDColumns) {
    readsToSpeciesContent = (
      <Grid item xs={12} sm={2}>
        <p style={{ color: detailContentColor, marginTop: '5px', marginBottom: '5px' }}><small>
          <b>{identifiedSpecies.attributes.reads_to_species ? identifiedSpecies.attributes.reads_to_species.toLocaleString() : '-'} read pairs</b>
            <br />
          matching organism
        </small></p>
      </Grid>
    );
  }

  let fractionMegabasesToGenusContent = null;
  if (!isDiagnosticView && isIllumina && false) {
    fractionMegabasesToGenusContent = (
      <Grid item xs={12} sm={2}>
        <p style={{ color: detailContentColor, marginTop: '5px', marginBottom: '5px' }}><small>
          <b>{identifiedSpecies.attributes.reads_frac_agreeing_megabases ? (identifiedSpecies.attributes.reads_frac_agreeing_megabases * 100).toFixed(2) : '-'}%</b>
            <br />
          matching genus
        </small></p>
      </Grid>
    );
  }

  let megabasesToSpeciesContent = null;
  if (!isDiagnosticView) {
      let megabasesToSpeciesValue = null
      let readsToSpeciesValue = null
      let percentMegabasesToSpeciesValue = null
      if (identifiedSpecies.attributes.reads_megabases_to_species) {
          megabasesToSpeciesValue = (
            identifiedSpecies.attributes.reads_megabases_to_species.toFixed(3)
          )

          percentMegabasesToSpeciesValue = totalMicrobialMegabases ? ((
            identifiedSpecies.attributes.reads_megabases_to_species / totalMicrobialMegabases
          ) * 100).toFixed(2) : null
      }

      if (identifiedSpecies.attributes.reads_to_species) {
        readsToSpeciesValue = identifiedSpecies.attributes.reads_to_species.toLocaleString()
      }

    megabasesToSpeciesContent = (
      <Grid item xs={12} sm={3}>
        <p style={{ color: detailContentColor, marginTop: '5px', marginBottom: '5px' }}><small>
          <b>
            {megabasesToSpeciesValue ?? '-'} MBs {percentMegabasesToSpeciesValue ? `(${percentMegabasesToSpeciesValue}%)` : null} {(readsToSpeciesValue && identifiedSpecies.attributes.species_call_raw_value !== null) ? `/ ${readsToSpeciesValue < 1000 ? readsToSpeciesValue : numeral(readsToSpeciesValue).format('0.0a')} ${isIllumina ? "read pairs" : "reads"}` : null}
        </b>
            <br />
          matching organism
        </small></p>
      </Grid>
    );
  }

  let coverageBreadthContent = null;
  if (!isDiagnosticView && showAlignmentMetrics && showCoverageBreadth && !isIllumina && identifiedSpecies.attributes.species_call_positive && !identifiedSpeciesIsVirus(identifiedSpecies)) {
    coverageBreadthContent = (
      <Grid item xs={12} sm={2}>
        <p style={{ color: detailContentColor, marginTop: '5px', marginBottom: '5px' }}><small>
          {identifiedSpecies.attributes.perc_nonzero_coverage ? (identifiedSpecies.attributes.perc_nonzero_coverage * 100).toFixed(2) : '-'}
          %<br/>reference coverage</small>
        </p>
      </Grid>
    );
  }

  let coverageDepthContent = null;
  if (!isDiagnosticView && showAlignmentMetrics && showCoverageDepth && !isIllumina && identifiedSpecies.attributes.species_call_positive && !identifiedSpeciesIsVirus(identifiedSpecies)) {
    coverageDepthContent = (
      <Grid item xs={12} sm={2}>
        <p style={{ color: detailContentColor, marginTop: '5px', marginBottom: '5px' }}><small>
          {identifiedSpecies.attributes.avg_depth ? (identifiedSpecies.attributes.avg_depth).toFixed(2) : '-'}
          <br/>average depth</small>
        </p>
      </Grid>
    );
  }

  let assemblyMegabasesToSpeciesContent = null;
  if (!isDiagnosticView && showAssemblyMetrics && showAssemblyMBsToSpecies && isIllumina && !identifiedSpeciesIsVirus(identifiedSpecies)) {
    assemblyMegabasesToSpeciesContent = (
      <Grid item xs={12} sm={2}>
        <p style={{ color: detailContentColor, marginTop: '5px', marginBottom: '5px' }}><small>
          {identifiedSpecies.attributes.assembly_megabases_to_species ? (identifiedSpecies.attributes.assembly_megabases_to_species).toLocaleString() : '-'} MBs from raw
          <br/> assembly matching organism</small>
        </p>
      </Grid>
    );
  }

  let assemblyContigsToSpeciesContent = null;
  if (!isDiagnosticView && showAssemblyMetrics && showContigsToSpecies && isIllumina && !identifiedSpeciesIsVirus(identifiedSpecies)) {
    assemblyContigsToSpeciesContent = (
      <Grid item xs={12} sm={showKIDColumns ? 2 : 3}>
        <p style={{ color: detailContentColor, marginTop: '5px', marginBottom: '5px' }}><small>
          {identifiedSpecies.attributes.contigs_to_species ? (identifiedSpecies.attributes.contigs_to_species).toLocaleString() : '-'} contigs from raw
          <br/>assembly matching organism</small>
        </p>
      </Grid>
    );
  }


  return (
    <Box>
      <Grid
        container
        sx={{
          pl: 1,
          pr: 1,
          textAlign: 'center',
          borderBottom: '#bdbdbd 2px solid'
        }}
      >
        <Grid item xs={12} sm={2} sx={{ textAlign: 'left' }}>
          <Tooltip title={identifiedSpecies.attributes.species} placement="top">
            {speciesNameContent}
          </Tooltip>
        </Grid>
        {positivityContent}
        {kidValueContent}
        {readsToSpeciesContent}
        {megabasesToSpeciesContent}
        {fractionMegabasesToGenusContent}
        {coverageBreadthContent}
        {coverageDepthContent}
        {assemblyMegabasesToSpeciesContent}
        {assemblyContigsToSpeciesContent}
      </Grid>
      {amrPredictionsContent}
    </Box>
  );
}

export default IdentifiedSpeciesRow;
